/// <reference types="vite/client" />
/**
 * OverviewStats — 5 KPI tiles + funnel chart, polled every 10s.
 * Phase 11.5 S1 + S2.
 */

import { useCallback, useEffect, useState } from "react";
import { useAgentSocketContext } from "../hooks/useAgentSocketContext";

interface Stats {
  active_sequences: number;
  posts_published_7d: number;
  comments_made_24h: number;
  replies_caught_7d: number;
  hot_leads: number;
  hot_leads_top: Array<{ id: string; company_name: string; warm_score: number }>;
}

interface FunnelStage {
  name: string;
  count: number;
  conversion_from_previous: number;
}

interface Funnel {
  days: number;
  stages: FunnelStage[];
}

export function OverviewStats() {
  const { subscribe } = useAgentSocketContext();
  const [stats, setStats] = useState<Stats | null>(null);
  const [funnel, setFunnel] = useState<Funnel | null>(null);
  const [funnelDays, setFunnelDays] = useState(30);

  const load = useCallback(async () => {
    try {
      const [statsRes, funnelRes] = await Promise.all([
        fetch("/api/overview/stats"),
        fetch(`/api/overview/funnel?days=${funnelDays}`),
      ]);
      if (statsRes.ok) setStats(await statsRes.json() as Stats);
      if (funnelRes.ok) setFunnel(await funnelRes.json() as Funnel);
    } catch {
      // best-effort; the panel keeps the previous data on the screen
    }
  }, [funnelDays]);

  // Phase 13 U2 #294 — bootstrap once, then refresh on realtime events
  // that change the counters (sequence advances, posts, comments, replies).
  // Funnel still refetches when the days filter changes.
  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const refresh = () => { void load(); };
    const unsubs = [
      subscribe("marketing.sequence_advanced", refresh),
      subscribe("marketing.post_published", refresh),
      subscribe("marketing.comment_made", refresh),
      subscribe("growth.pharow_enrolled", refresh),
      subscribe("growth.lead_scored", refresh),
      subscribe("support.email_correlated", refresh),
      subscribe("support.auto_reply_sent", refresh),
    ];
    return () => { unsubs.forEach((u) => u()); };
  }, [subscribe, load]);

  const maxCount = funnel ? Math.max(1, ...funnel.stages.map((s) => s.count)) : 1;

  return (
    <section className="space-y-6">
      <div className="section-rule">
        <p className="eyebrow">// Growth Agent — Live</p>
      </div>

      {/* 5 tiles — 2 cols mobile, 3 cols sm, 5 cols lg. Last tile spans full
          width on the lone row in 2-col layout so it doesn't sit orphaned. */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 [&>*:last-child]:col-span-2 sm:[&>*:last-child]:col-span-1">
        <Tile label="Sequences actives" value={stats?.active_sequences ?? "—"} />
        <Tile label="Posts (7j)" value={stats?.posts_published_7d ?? "—"} />
        <Tile label="Commentaires (24h)" value={stats?.comments_made_24h ?? "—"} />
        <Tile label="Réponses (7j)" value={stats?.replies_caught_7d ?? "—"} accent="var(--color-status-active)" />
        <Tile label="Leads chauds (>30)" value={stats?.hot_leads ?? "—"} accent="var(--color-accent-bright)" />
      </div>

      {/* Hot leads list */}
      {stats?.hot_leads_top && stats.hot_leads_top.length > 0 && (
        <div className="panel p-4">
          <p className="eyebrow mb-2">Top 5 leads chauds</p>
          <ul className="space-y-1 text-sm">
            {stats.hot_leads_top.map((l) => (
              <li key={l.id} className="flex items-center justify-between font-mono text-[12px]">
                <span className="text-[var(--color-text)] truncate">{l.company_name}</span>
                <span className="text-[var(--color-accent-bright)] tabular-nums">🌡 {l.warm_score}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Funnel chart */}
      <div className="panel p-5">
        <div className="flex items-center justify-between mb-4">
          <p className="eyebrow">Funnel agrégé</p>
          <select
            value={funnelDays}
            onChange={(e) => setFunnelDays(Number(e.target.value))}
            className="font-mono text-xs px-2 py-1 rounded"
            style={{ background: "var(--color-bg-card)", color: "var(--color-text)", border: "1px solid var(--color-border)" }}
          >
            <option value={7}>7j</option>
            <option value={30}>30j</option>
            <option value={90}>90j</option>
          </select>
        </div>
        {funnel ? (
          <div className="space-y-2">
            {funnel.stages.map((s, i) => {
              const widthPct = (s.count / maxCount) * 100;
              const convPct = Math.round(s.conversion_from_previous * 100);
              return (
                <div key={s.name} className="space-y-1">
                  <div className="flex items-center justify-between font-mono text-xs">
                    <span className="text-[var(--color-text)]">{s.name}</span>
                    <span className="text-[var(--color-text-muted)] tabular-nums">
                      {s.count}{i > 0 && <span className="ml-2 text-[var(--color-text-dim)]">{convPct}%</span>}
                    </span>
                  </div>
                  <div className="h-4 rounded" style={{ background: "var(--color-bg)" }}>
                    <div
                      className="h-full rounded transition-all"
                      style={{
                        width: `${widthPct}%`,
                        background: `linear-gradient(90deg, var(--color-accent-bright), var(--color-accent))`,
                      }}
                    />
                  </div>
                </div>
              );
            })}
            <p className="mt-3 font-mono text-[10px] text-[var(--color-text-dim)]">
              ⓘ Stage "Ouvert" inclut les fetches Gmail proxy (1 par vue) — moins précis que les opens réels desktop/Apple Mail.
            </p>
          </div>
        ) : (
          <p className="font-mono text-xs text-[var(--color-text-dim)] blink">Loading funnel…</p>
        )}
      </div>
    </section>
  );
}

function Tile({ label, value, accent }: { label: string; value: number | string; accent?: string }) {
  return (
    <div className="panel p-3 sm:p-4">
      <p className="eyebrow">{label}</p>
      <p
        className="font-display italic text-2xl sm:text-3xl lg:text-[36px] leading-[1.05] mt-1 tabular-nums"
        style={{ color: accent ?? "var(--color-text)" }}
      >
        {value}
      </p>
    </div>
  );
}
